import { useSortable } from '@dnd-kit/sortable';
import {
  Box,
  ClickAwayListener,
  Fade,
  Paper,
  Popper,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useState, useMemo, useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { positiveTrendColorState, negativeTrendColorState } from 'states';
import { OptionType } from 'types';
import { PositionLeg } from 'types/optionsCalculator';
import { CSS } from '@dnd-kit/utilities';
import { formatStrikeIndicatorExpiration } from 'util/optionsCalculator';

// Memoized sortable selected strike component
const SortableSelectedStrike = React.memo(
  ({
    strike,
    leg,
    placement,
  }: {
    strike: number;
    position: number;
    leg: PositionLeg;
    placement: 'above' | 'below';
  }) => {
    const theme = useTheme();
    const [isHovered, setIsHovered] = useState(false);
    const [popperOpen, setPopperOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const posServerColor = useRecoilValue(positiveTrendColorState);
    const negServerColor = useRecoilValue(negativeTrendColorState);

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: leg.id,
      data: { type: 'leg', strike, leg },
    });

    const style = useMemo(
      () => ({
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative' as const,
        cursor: isDragging ? 'grabbing' : isHovered ? 'grab' : 'pointer',
        zIndex: isDragging ? 10 : isHovered ? 5 : 3,
        marginTop: placement === 'above' ? 0 : '4px',
        marginBottom: placement === 'below' ? 0 : '4px',
        pointerEvents: 'auto' as const,
      }),
      [transform, transition, isDragging, isHovered, placement],
    );

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        if (!isDragging) {
          setAnchorEl(e.currentTarget);
          setPopperOpen(!popperOpen);
        }
      },
      [popperOpen, isDragging],
    );

    const handleClose = useCallback(() => {
      setPopperOpen(false);
      setAnchorEl(null);
    }, []);

    const optionType = leg.type === OptionType.CALL ? 'C' : 'P';
    const displayText = `${strike}${optionType}`;
    const isBuy = leg.quantity > 0;

    return (
      <>
        <Box
          ref={setNodeRef}
          style={style}
          {...attributes}
          {...listeners}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={handleClick}
        >
          <Box
            sx={{
              position: 'relative',
              backgroundColor: isBuy
                ? isHovered
                  ? posServerColor
                  : posServerColor
                : isHovered
                  ? negServerColor
                  : negServerColor,
              color: isBuy
                ? theme.palette.getContrastText(posServerColor)
                : theme.palette.getContrastText(negServerColor),
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              boxShadow: isHovered ? theme.shadows[3] : theme.shadows[1],
              border: isHovered
                ? `1px solid ${isBuy ? posServerColor : negServerColor}`
                : 'none',
              transform: isHovered ? 'scale(1.05)' : 'scale(1)',
              transition: 'all 0.2s ease-in-out',
              '&::after':
                placement === 'above'
                  ? {
                      content: '""',
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: 0,
                      borderLeft: '4px solid transparent',
                      borderRight: '4px solid transparent',
                      borderTop: `4px solid ${
                        isBuy
                          ? isHovered
                            ? posServerColor
                            : posServerColor
                          : isHovered
                            ? negServerColor
                            : negServerColor
                      }`,
                    }
                  : {
                      content: '""',
                      position: 'absolute',
                      bottom: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: 0,
                      borderLeft: '4px solid transparent',
                      borderRight: '4px solid transparent',
                      borderBottom: `4px solid ${
                        isBuy
                          ? isHovered
                            ? posServerColor
                            : posServerColor
                          : isHovered
                            ? negServerColor
                            : negServerColor
                      }`,
                    },
            }}
          >
            {/* Expiration tag */}
            {leg.expiration && (
              <Box
                sx={{
                  position: 'absolute',
                  ...(isBuy
                    ? { top: -8, right: -6 }
                    : { bottom: -8, right: -6 }),
                  backgroundColor: theme.palette.info.main,
                  color: theme.palette.info.contrastText,
                  fontSize: '0.55rem',
                  fontWeight: 'bold',
                  padding: '1px 3px',
                  borderRadius: '3px',
                  lineHeight: 1,
                  minWidth: '16px',
                  textAlign: 'center',
                  boxShadow: theme.shadows[1],
                  border: `1px solid ${theme.palette.background.paper}`,
                }}
              >
                {formatStrikeIndicatorExpiration(leg.expiration)}
              </Box>
            )}
            {displayText}
          </Box>
        </Box>

        <Popper
          open={popperOpen && anchorEl !== null}
          anchorEl={anchorEl}
          placement="top"
          transition
          style={{ zIndex: 1300 }}
          disablePortal={false}
          modifiers={[
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                altAxis: true,
                altBoundary: true,
                tether: true,
                rootBoundary: 'document',
                padding: 8,
              },
            },
            {
              name: 'flip',
              enabled: true,
              options: {
                altBoundary: true,
                rootBoundary: 'document',
                padding: 8,
              },
            },
          ]}
        >
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={handleClose}>
              <Fade {...TransitionProps} timeout={200}>
                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    boxShadow: theme.shadows[8],
                    minWidth: 200,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', mb: 1 }}
                  >
                    {leg.sym} {strike} {leg.type.toUpperCase()}
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                  >
                    <Typography variant="caption">
                      <strong>Quantity:</strong> {leg.quantity}
                    </Typography>
                    <Typography variant="caption">
                      <strong>Price:</strong> ${leg.price?.toFixed(2) || 'N/A'}
                    </Typography>
                    {leg.expiration && (
                      <Typography variant="caption">
                        <strong>Expiration:</strong>{' '}
                        {formatStrikeIndicatorExpiration(leg.expiration)}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Fade>
            </ClickAwayListener>
          )}
        </Popper>
      </>
    );
  },
);

export default SortableSelectedStrike;
